try {
    if (process.env.TOKENS_FEATURE_FLAG === 'true' || process.env.STORYBOOK_TOKENS_FEATURE_FLAG !== 'false') require('./index-new.css');
    else require('./index.css');
  } catch (e) {
    if (e.code !== 'MODULE_NOT_FOUND') {
      throw e; // Re-throw unexpected errors
    }
    console.warn('index.css / index-new.css not found, proceeding without it.');
  }


import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Container } from '@design-system/container';
import { SearchBlock } from '@design-system/search-block';
import { Section } from '@design-system/section';
import classNames from 'classnames';
import React, { useState, useEffect, useMemo, useCallback, useRef, forwardRef } from 'react';
import { Link } from '@design-system/link';
import { Button } from '@design-system/button';
import FocusTrap from 'focus-trap-react';
import { ExtendableImage } from '@design-system/extendable-image';
import { Tooltip } from '@design-system/tooltip';
import { isBrowser } from '@phx-husky/browser-helpers';
import { useScreenRecognition } from '@phx-husky/use-screen-recognition';
import { Typography } from '@design-system/typography';
import { Icon } from '@design-system/icon';

const BurgerCloser = ({ burgerCloseIcon, burgerCloseLabel, isJS, }) => !isJS ? (jsx(Link, Object.assign({ className: "cds-global-header__burger-closer", variant: "subtle-icon", href: "#close-menu", size: "large", iconSuffix: {
        props: burgerCloseIcon,
    } }, { children: burgerCloseLabel }))) : null;

const BurgerOpener = ({ burgerCloseIcon, burgerOpenLabel, burgerOpenIcon, burgerToggleLabel, handleBurgerClick, isOpen, isJS, burgerBtnRef, isMobileView, }) => {
    if (isJS && isMobileView)
        return (jsx(Button, Object.assign({ ref: burgerBtnRef, variant: "subtle-icon", className: "cds-navigation-mobile__burger", size: "large", iconSuffix: {
                props: isOpen ? burgerCloseIcon : burgerOpenIcon,
            }, onClick: handleBurgerClick, "aria-expanded": !!isOpen }, { children: burgerToggleLabel })));
    if (isJS && !isMobileView) {
        return null;
    }
    return (jsx(Link, Object.assign({ className: "cds-global-header__burger-opener", variant: "subtle-icon", href: "#open-menu", size: "large", iconSuffix: {
            props: burgerOpenIcon,
        } }, { children: burgerOpenLabel })));
};

const CartIcon = ({ ariaLabel, href, LinkComponent = Link, itemsQuantity, }) => (jsx(LinkComponent, Object.assign({ className: classNames('cds-shopping-cart-icon', {
        'cds-shopping-cart-icon--no-items': itemsQuantity === 0,
    }), variant: "subtle-icon", size: "small", href: href, iconSuffix: { props: { icon: 'shoppingCart' } }, "aria-label": ariaLabel }, { children: itemsQuantity })));

const FocusTrapWrapper = ({ isOpen, isJS, isMobileView, children, }) => {
    const Wrapper = isOpen && isJS && isMobileView ? FocusTrap : React.Fragment;
    return jsx(Wrapper, { children: children });
};

const Logo = ({ className, image, link, onClick }) => {
    const LinkComponent = (link === null || link === void 0 ? void 0 : link.component) || Link;
    const linkProps = link === null || link === void 0 ? void 0 : link.props;
    return (jsx("div", Object.assign({ className: classNames('cds-logo', className) }, { children: link && linkProps ? (jsx(LinkComponent, Object.assign({ onClick: onClick }, linkProps, { className: "cds-logo__link", variant: "wrapper", rel: "home" }, { children: jsx(ExtendableImage, Object.assign({}, image)) }))) : (jsx(ExtendableImage, Object.assign({}, image))) })));
};

const itemClassName = 'cds-global-header__navigation-item';
const moreClassName = 'cds-global-header__more-item';
const languageSwitchClassName = 'cds-global-header__lang-switch';
const logoClassName = 'cds-global-header__navigation-logo';
const searchClassName = 'cds-global-header__navigation-search';
const cartIconClassName = 'cds-global-header__cart-icon';
const profileClassName = 'cds-global-header__navigation-profile';
const blurDelay = 100;
const keySpacebar = 'Spacebar';
const keyEnter = 'Enter';

const useBrowserScreen = () => {
    const { isMobile, isTablet, isDesktop } = useScreenRecognition();
    const isMobileView = isBrowser() && isMobile;
    const isTabletView = isBrowser() && isTablet;
    const isDesktopView = isBrowser() && isDesktop;
    return { isMobileView, isTabletView, isDesktopView };
};

const useJS = () => {
    const [isJS, setIsJS] = useState(false);
    useEffect(() => {
        setIsJS(!!typeof window);
    }, []);
    return [isJS];
};

const useDebounceFunc = (func, milliseconds = 400) => {
    const time = milliseconds;
    let timer;
    return (event) => {
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(func, time, event);
    };
};

const useNavigationResize = ({ listRef, navRef, navigationData, maxVisibleNavItems = 0, }) => {
    const { isMobileView } = useBrowserScreen();
    const validNavigationData = useMemo(() => navigationData.filter((x) => {
        var _a;
        return x.href ||
            ('submenu' in x && Boolean(x.submenu)) ||
            ('subItems' in x && Boolean((_a = x.subItems) === null || _a === void 0 ? void 0 : _a.length));
    }), [navigationData]);
    const [visibleItems, setVisibleItems] = useState(validNavigationData);
    const [hiddenItems, setHiddenItems] = useState([]);
    const [widthData, setWidthData] = useState(null);
    const [logoWidth, setLogoWidth] = useState(0);
    const [searchWidth, setSearchWidth] = useState(0);
    const [cartIconWidth, setCartIconWidth] = useState(0);
    const [profileWidth, setProfileWidth] = useState(0);
    const [isReady, setIsReady] = useState(false);
    const handleResize = useCallback(() => {
        if (!widthData || !navRef.current)
            return;
        const navElWidth = navRef.current.offsetWidth;
        const { navItems, langSwitchBtnWidth } = widthData;
        const moreItemElement = navRef.current.getElementsByClassName(moreClassName)[0];
        const moreBtnWidth = (moreItemElement === null || moreItemElement === void 0 ? void 0 : moreItemElement.getBoundingClientRect().width) || 0;
        const totalStaticWidth = logoWidth +
            searchWidth +
            cartIconWidth +
            profileWidth +
            langSwitchBtnWidth;
        let currentSum = totalStaticWidth;
        const canAddMore = (count) => !maxVisibleNavItems || count < maxVisibleNavItems;
        const getInitialVisibleHidden = () => {
            const visible = [];
            const hidden = [];
            navItems.forEach((width, index) => {
                const availableSpace = navElWidth - currentSum;
                if (availableSpace >= width && canAddMore(visible.length)) {
                    currentSum += width || 0;
                    visible.push(validNavigationData[index]);
                }
                else {
                    hidden.push(validNavigationData[index]);
                }
            });
            return { visible, hidden };
        };
        const getFinalVisibleHidden = () => {
            currentSum = totalStaticWidth + moreBtnWidth;
            const visible = [];
            const hidden = [];
            for (let i = 0; i < navItems.length; i += 1) {
                const navItemWidth = navItems[i];
                const availableSpace = navElWidth - currentSum;
                if (availableSpace >= navItemWidth && canAddMore(visible.length)) {
                    currentSum += navItemWidth;
                    visible.push(validNavigationData[i]);
                }
                else {
                    hidden.push(...validNavigationData.slice(i));
                    break;
                }
            }
            return { visible, hidden };
        };
        const { visible: initialVisible, hidden: initialHidden } = getInitialVisibleHidden();
        if (initialHidden.length > 0 ||
            (maxVisibleNavItems && initialVisible.length > maxVisibleNavItems)) {
            const { visible, hidden } = getFinalVisibleHidden();
            setVisibleItems(visible);
            setHiddenItems(hidden);
        }
        else {
            setVisibleItems(initialVisible);
            setHiddenItems(initialHidden);
        }
        setIsReady(true);
    }, [
        widthData,
        logoWidth,
        searchWidth,
        cartIconWidth,
        profileWidth,
        navRef,
        validNavigationData,
        maxVisibleNavItems,
    ]);
    const debouncedHandleResize = useDebounceFunc(handleResize, 400);
    useEffect(() => {
        if (!isMobileView) {
            window.addEventListener('resize', debouncedHandleResize);
        }
        else {
            setIsReady(true);
            window.removeEventListener('resize', debouncedHandleResize);
        }
        return () => {
            window.removeEventListener('resize', debouncedHandleResize);
        };
    }, [debouncedHandleResize, isMobileView]);
    useEffect(() => {
        if ((listRef === null || listRef === void 0 ? void 0 : listRef.current) && !isMobileView) {
            const children = Array.from(listRef.current.children);
            const visibleItemsCount = children.filter(({ className }) => className.includes(itemClassName) &&
                !className.includes(moreClassName) &&
                !className.includes(languageSwitchClassName)).length;
            if (visibleItemsCount < validNavigationData.length) {
                return;
            }
            const navItems = [];
            let langSwitchBtnWidth = 0;
            children.forEach((item) => {
                if (item.className.includes(itemClassName) &&
                    !item.className.includes(moreClassName) &&
                    !item.className.includes(languageSwitchClassName)) {
                    const rect = item.getBoundingClientRect();
                    navItems.push(rect.width);
                }
                if (item.className.includes(languageSwitchClassName)) {
                    langSwitchBtnWidth = item.getBoundingClientRect().width;
                }
            });
            setWidthData({ navItems, langSwitchBtnWidth });
        }
    }, [listRef, validNavigationData, isMobileView]);
    useEffect(() => {
        requestAnimationFrame(() => {
            handleResize();
        });
    }, [handleResize, isMobileView]);
    useEffect(() => {
        let resizeObserver = null;
        if (listRef.current && !isMobileView) {
            resizeObserver = new ResizeObserver(([entry]) => {
                const children = Array.from(entry.target.children);
                const logo = children.find((el) => el.className.includes(logoClassName));
                const search = children.find((el) => el.className.includes(searchClassName));
                const cartIcon = children.find((el) => el.className.includes(cartIconClassName));
                const profile = children.find((el) => el.className.includes(profileClassName));
                if (logo) {
                    setLogoWidth(logo.getBoundingClientRect().width);
                }
                if (search) {
                    setSearchWidth(search.getBoundingClientRect().width);
                }
                if (cartIcon) {
                    setCartIconWidth(cartIcon.getBoundingClientRect().width);
                }
                if (profile) {
                    setProfileWidth(profile.getBoundingClientRect().width);
                }
                handleResize();
            });
            resizeObserver.observe(listRef.current);
            return () => {
                resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.disconnect();
            };
        }
        return undefined;
    }, [listRef, isMobileView, handleResize, validNavigationData]);
    return {
        visibleItems,
        hiddenItems,
        isReady,
    };
};

const shouldBeRendered = (data, conditions, operator = 'or') => {
    if (!data)
        return false;
    if (operator === 'or') {
        return conditions.some((c) => c);
    }
    if (operator === 'and') {
        return conditions.every((c) => c);
    }
    return false;
};

function __rest(s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

const useDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);
    const timer = useRef();
    const { isMobileView } = useBrowserScreen();
    const handleShowMoreClick = (e) => {
        e.preventDefault();
        setIsOpen((prevVal) => !prevVal);
    };
    const handleMenuItemClick = (e, hasSubitems, handleMobileNavigationItemClick) => {
        if (!isMobileView) {
            return;
        }
        if (hasSubitems) {
            e.preventDefault();
            setIsOpen((prevVal) => !prevVal);
        }
        else {
            handleMobileNavigationItemClick();
        }
    };
    const handleMouseOver = (e) => {
        e.preventDefault();
        if (!isOpen) {
            setIsOpen(true);
        }
    };
    const handleKeyPress = (e) => {
        if (e.key === ' ' || e.key === keySpacebar || e.key === keyEnter) {
            e.preventDefault();
            setIsOpen((prevVal) => !prevVal);
        }
    };
    const handleDesktopBlur = () => {
        if (isMobileView) {
            return;
        }
        timer.current = setTimeout(() => {
            setIsOpen(false);
        }, blurDelay);
    };
    const handleDesktopOutsideBlur = (e) => {
        if (e.currentTarget.contains(e.relatedTarget) && !isMobileView) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        handleDesktopBlur();
    };
    const handleFocus = () => {
        if (timer.current)
            clearTimeout(timer.current);
    };
    const handleEsc = (event) => {
        if (event.key === 'Escape') {
            if (timer.current)
                clearTimeout(timer.current);
            setIsOpen(false);
        }
    };
    useEffect(() => {
        window.addEventListener('keyup', handleEsc);
        return () => {
            window.removeEventListener('keyup', handleEsc);
        };
    }, []);
    return {
        handleDesktopBlur,
        handleDesktopOutsideBlur,
        handleFocus,
        handleShowMoreClick,
        handleMenuItemClick,
        handleKeyPress,
        handleMouseOver,
        isOpen,
    };
};

const getLinkIcon = (openIcon, closeIcon, isOpen, shouldShow = true) => shouldShow && openIcon && closeIcon
    ? {
        props: isOpen ? closeIcon : openIcon,
    }
    : undefined;

const getLinkSize = (smallFactor) => smallFactor ? 'large' : 'small';

const LanguageSwitch = ({ title, ariaLabel, languageData, linkNavElement: LinkElement = Link, openIcon, closeIcon, isMobileView, }) => {
    const { handleDesktopBlur, handleDesktopOutsideBlur, handleFocus, handleShowMoreClick, handleKeyPress, isOpen, handleMouseOver, } = useDropdown();
    const onMouseOverDesktop = !isMobileView ? handleMouseOver : undefined;
    return (jsxs("li", Object.assign({ className: classNames('cds-global-header__navigation-item', 'cds-global-header__navigation-item--has-children', languageSwitchClassName), onBlur: handleDesktopOutsideBlur, onMouseLeave: handleDesktopBlur }, { children: [jsx(Button, Object.assign({ onKeyDown: handleKeyPress, onClick: handleShowMoreClick, onBlur: handleDesktopBlur, onMouseOver: onMouseOverDesktop, variant: "nav", size: getLinkSize(isMobileView), "aria-label": ariaLabel, "aria-expanded": isOpen, className: classNames('cds-global-header__lang-switch-btn', {
                    active: isOpen,
                }), iconPrefix: {
                    props: {
                        icon: 'translation',
                        className: 'cds-global-header__lang-switch-icon',
                    },
                }, iconSuffix: getLinkIcon(openIcon, closeIcon, isOpen, !isMobileView) }, { children: title })), isMobileView ? (jsx(Button, Object.assign({ variant: "primary-icon", onClick: handleShowMoreClick, onKeyDown: handleKeyPress, size: "small", className: "cds-global-header__navigation-arrow", "aria-expanded": isOpen, iconSuffix: getLinkIcon(openIcon, closeIcon, isOpen) }, { children: title }))) : null, jsx("div", Object.assign({ className: "cds-global-header__navigation-drop" }, { children: jsx("ul", Object.assign({ className: "cds-global-header__lang-list" }, { children: languageData.map((_a) => {
                        var { label, href, locale, translated } = _a, item = __rest(_a, ["label", "href", "locale", "translated"]);
                        return (jsx("li", { children: translated ? (jsx(LinkElement, Object.assign({ className: "cds-global-header__lang-link" }, item, { href: href, onClick: handleDesktopBlur, variant: "nav", size: getLinkSize(isMobileView), onFocus: handleFocus, onBlur: handleDesktopBlur }, { children: label }))) : (jsx(Typography, { className: "cds-global-header__lang-item", content: label, tag: "span", variant: "body" })) }, locale));
                    }) })) }))] })));
};

const isActive = (activePath, linkHref, subLinks) => subLinks.some((el) => activePath.includes(el.href)) ||
    activePath === linkHref;

const SubNavigation = ({ className, subItems, linkNavElement: LinkElement = Link, subMenuLabel, activeUrlPath, handleFocus, handleBlur, isMobileView, href, ariaLabel, handleMobileNavigationItemClick, onClickSubnavItem, }) => {
    const handleClick = (subLabel) => {
        if (onClickSubnavItem)
            onClickSubnavItem(subLabel);
        if (isMobileView)
            handleMobileNavigationItemClick();
    };
    return (jsx("div", Object.assign({ className: classNames('cds-global-header__navigation-drop', className) }, { children: jsxs("ul", Object.assign({ className: "cds-global-header__sub-list" }, { children: [shouldBeRendered(subMenuLabel, [isMobileView, Boolean(href)], 'and') && (jsx("li", Object.assign({ className: "cds-navigation__sub-item" }, { children: jsx(LinkElement, Object.assign({ className: classNames('cds-navigation__sub-link', {
                            active: activeUrlPath === href,
                        }), "aria-current": activeUrlPath === href, "aria-label": ariaLabel, onClick: handleMobileNavigationItemClick, href: href, variant: "nav", size: "large", onFocus: handleFocus, onBlur: handleBlur }, { children: subMenuLabel })) }))), subItems.map((_a) => {
                    var { label: subLabel, href: subHref, ariaLabel: subAriaLabel, subMenuLabel: _subMenuLabel, subItems: _subItems } = _a, subItem = __rest(_a, ["label", "href", "ariaLabel", "subMenuLabel", "subItems"]);
                    return (jsx("li", { children: jsx(LinkElement, Object.assign({ className: classNames('cds-global-header__sub-link', {
                                active: activeUrlPath === subHref,
                            }), "aria-current": activeUrlPath === subHref }, subItem, { "aria-label": subAriaLabel, onClick: () => handleClick(subLabel), href: subHref, variant: "nav", size: getLinkSize(isMobileView), onFocus: handleFocus, onBlur: handleBlur }, { children: subLabel })) }, subLabel));
                })] })) })));
};

const NavigationBasicItem = ({ className, linkNavElement, activeUrlPath, href = '', ariaLabel, isMobileView, label, subItems, subMenuLabel, openIcon, closeIcon, expandTitle, onClickNavItem, handleMobileNavigationItemClick, }) => {
    const LinkElement = linkNavElement || Link;
    const { handleDesktopBlur, handleDesktopOutsideBlur, handleFocus, handleMenuItemClick, handleKeyPress, isOpen, handleMouseOver, } = useDropdown();
    const hasSubItems = Boolean(subItems === null || subItems === void 0 ? void 0 : subItems.length);
    const onMouseOverDesktop = hasSubItems && !isMobileView ? handleMouseOver : undefined;
    const handleClick = (e) => {
        onClickNavItem === null || onClickNavItem === void 0 ? void 0 : onClickNavItem(label);
        handleMenuItemClick(e, hasSubItems, handleMobileNavigationItemClick);
    };
    return (jsxs("li", Object.assign({ className: classNames('cds-global-header__navigation-item', className, {
            'cds-global-header__navigation-item--has-children': hasSubItems,
            active: href && isActive(activeUrlPath, href, subItems || []),
        }), onBlur: handleDesktopOutsideBlur, onMouseLeave: handleDesktopBlur }, { children: [href ? (jsxs(Fragment, { children: [jsx(LinkElement, Object.assign({ className: classNames('cds-global-header__navigation-link', {
                            active: isActive(decodeURIComponent(activeUrlPath), href, subItems || []),
                            'has-submenu': hasSubItems,
                        }), onClick: handleClick, "aria-current": decodeURIComponent(activeUrlPath) === href, onMouseOver: onMouseOverDesktop, href: href, variant: "nav", size: getLinkSize(isMobileView), "aria-label": ariaLabel }, { children: label })), hasSubItems && (jsx(Button, Object.assign({ variant: isMobileView ? 'primary-icon' : 'nav', onClick: handleClick, onKeyDown: handleKeyPress, onMouseOver: onMouseOverDesktop, size: "small", className: "cds-global-header__navigation-arrow", "aria-expanded": isOpen, iconSuffix: getLinkIcon(openIcon, closeIcon, isOpen) }, { children: `${expandTitle} ${label}` })))] })) : (jsx(Button, Object.assign({ className: classNames('cds-global-header__navigation-btn', {
                    'has-submenu': hasSubItems,
                }), variant: "nav", onClick: handleClick, onKeyDown: handleKeyPress, onMouseOver: onMouseOverDesktop, size: getLinkSize(isMobileView), "aria-label": ariaLabel, "aria-expanded": isOpen, iconSuffix: hasSubItems ? getLinkIcon(openIcon, closeIcon, isOpen) : undefined }, { children: label }))), hasSubItems && (jsx(SubNavigation, { subItems: subItems, activeUrlPath: decodeURIComponent(activeUrlPath), subMenuLabel: subMenuLabel, linkNavElement: linkNavElement, handleFocus: handleFocus, handleBlur: handleDesktopBlur, isMobileView: isMobileView, href: href, ariaLabel: ariaLabel, handleMobileNavigationItemClick: handleMobileNavigationItemClick, onClickSubnavItem: onClickNavItem }))] })));
};

const NavigationWithSplitterItem = forwardRef(({ className, linkNavElement, activeUrlPath, href = '', ariaLabel, isMobileView, label, submenu, openIcon, closeIcon, expandTitle, onClickNavItem, handleMobileNavigationItemClick, }, ref) => {
    const LinkElement = linkNavElement || Link;
    const { handleDesktopBlur, handleDesktopOutsideBlur, handleMenuItemClick, handleKeyPress, isOpen, handleMouseOver, } = useDropdown();
    const onMouseOverDesktop = submenu && !isMobileView ? handleMouseOver : undefined;
    const handleClick = (e) => {
        if (onClickNavItem)
            onClickNavItem(label);
        handleMenuItemClick(e, Boolean(submenu), handleMobileNavigationItemClick);
    };
    return (jsxs("li", Object.assign({ className: classNames('cds-global-header__navigation-item', className, {
            'cds-global-header__navigation-item--has-children': Boolean(submenu),
        }), onBlur: handleDesktopOutsideBlur, onMouseLeave: handleDesktopBlur }, { children: [href ? (jsxs(Fragment, { children: [jsx(LinkElement, Object.assign({ className: classNames('cds-global-header__navigation-link', {
                            'has-submenu': Boolean(submenu),
                            active: isActive(activeUrlPath, href, []),
                        }), onClick: handleClick, "aria-current": decodeURIComponent(activeUrlPath) === href, onMouseOver: onMouseOverDesktop, href: href, variant: "nav", size: getLinkSize(isMobileView), "aria-label": ariaLabel }, { children: label })), Boolean(submenu) && (jsx(Button, Object.assign({ variant: isMobileView ? 'primary-icon' : 'nav', onClick: handleClick, onKeyDown: handleKeyPress, onMouseOver: onMouseOverDesktop, size: "small", className: "cds-global-header__navigation-arrow", "aria-expanded": isOpen, iconSuffix: getLinkIcon(openIcon, closeIcon, isOpen) }, { children: `${expandTitle} ${label}` })))] })) : (jsx(Button, Object.assign({ className: classNames('cds-global-header__navigation-btn', {
                    'has-submenu': Boolean(submenu),
                }), "aria-current": decodeURIComponent(activeUrlPath) === href, onClick: handleClick, onMouseOver: onMouseOverDesktop, onKeyDown: handleKeyPress, variant: "nav", size: getLinkSize(isMobileView), "aria-label": ariaLabel, iconSuffix: submenu ? getLinkIcon(openIcon, closeIcon, isOpen) : undefined }, { children: label }))), Boolean(submenu) && (jsx(Section, Object.assign({ ref: ref, className: classNames('cds-global-header__sub-navigation', {
                    'cds-global-header__sub-navigation--open': isOpen,
                }) }, { children: jsx(Container, Object.assign({ maxWidth: "max-grid" }, { children: submenu })) })))] })));
});

const isWithSplitter = (props) => !!props.submenu;
const NavigationItem = (props) => isWithSplitter(props) ? (jsx(NavigationWithSplitterItem, Object.assign({}, props))) : (jsx(NavigationBasicItem, Object.assign({}, props)));

const Profile = ({ headline, greeting, icon: { icon }, account, register, logIn, logOut, signedIn, handleSignOut, handleLogIn, handleRegister, userName, linkNavElement: LinkElement = Link, isMobileView, }) => (jsx("div", Object.assign({ className: "cds-global-header-profile" }, { children: signedIn ? (jsxs(Fragment, { children: [jsx(Typography, { className: "cds-global-header-profile__greeting", variant: "body", weight: "medium", size: "m", tag: "p", content: `${greeting}${userName !== null && userName !== void 0 ? userName : ''} 👋` }), jsx(LinkElement, Object.assign({ className: "cds-global-header-profile__link" }, account)), jsx(Button, Object.assign({ className: "cds-global-header-profile__button cds-global-header-profile__button--log-out" }, logOut, { variant: isMobileView ? 'primary' : 'secondary', onClick: handleSignOut }))] })) : (jsxs(Fragment, { children: [jsxs("div", Object.assign({ className: "cds-global-header-profile__headline-wrapper" }, { children: [isMobileView ? (jsx(Icon, { className: "cds-global-header-profile__headline-icon", icon: icon })) : null, jsx(Typography, { className: "cds-global-header-profile__headline", variant: "body", weight: "medium", size: "m", tag: "p", content: headline })] })), jsxs("div", Object.assign({ className: "cds-global-header-profile__buttons-wrapper" }, { children: [jsx(Button, Object.assign({ className: "cds-global-header-profile__button cds-global-header-profile__button--register" }, register, { onClick: handleRegister })), jsx(Button, Object.assign({ className: "cds-global-header-profile__button cds-global-header-profile__button--log-in" }, logIn, { onClick: handleLogIn }))] }))] })) })));

const Navigation = ({ navigationName, navigationData, linkNavElement, onClickNavItem, className, closeIcon, openIcon, activeUrlPath, logo, moreBtn, handleMobileNavigationItemClick, languageSwitch, search, cartIcon, profile, maxVisibleNavItems = 0, }) => {
    const listRef = useRef(null);
    const navRef = useRef(null);
    const { isMobileView } = useBrowserScreen();
    const [isJS] = useJS();
    const { visibleItems, hiddenItems, isReady } = useNavigationResize({
        navigationData,
        listRef,
        navRef,
        maxVisibleNavItems,
    });
    return (jsx("nav", Object.assign({ ref: navRef, className: classNames('cds-global-header__navigation', className), "aria-label": navigationName }, { children: jsxs("ul", Object.assign({ ref: listRef, style: { visibility: isJS && !isReady ? 'hidden' : 'visible' }, className: "cds-global-header__navigation-list" }, { children: [logo && (jsx("li", Object.assign({ className: "cds-global-header__navigation-logo" }, { children: jsx(Logo, { className: "cds-global-header__desktop-logo", image: logo.imageLarge, link: logo.link }) }))), visibleItems.map((item) => (jsx(NavigationItem, Object.assign({ linkNavElement: linkNavElement, activeUrlPath: activeUrlPath, isMobileView: isMobileView, openIcon: openIcon, closeIcon: closeIcon, expandTitle: moreBtn.title, isJS: isJS, onClickNavItem: onClickNavItem, handleMobileNavigationItemClick: handleMobileNavigationItemClick }, item), item.label))), shouldBeRendered(moreBtn, [isJS, !isMobileView, (hiddenItems === null || hiddenItems === void 0 ? void 0 : hiddenItems.length) > 0], 'and') ? (jsx(NavigationBasicItem, { className: classNames(moreClassName, {
                        [`${moreClassName}--hidden`]: !navigationData.length,
                    }), linkNavElement: linkNavElement, activeUrlPath: activeUrlPath, isMobileView: isMobileView, openIcon: openIcon, closeIcon: closeIcon, expandTitle: moreBtn.title, subItems: hiddenItems, ariaLabel: moreBtn === null || moreBtn === void 0 ? void 0 : moreBtn.ariaLabel, handleMobileNavigationItemClick: handleMobileNavigationItemClick, isJS: isJS, label: moreBtn.title, onClickNavItem: onClickNavItem, href: "" })) : null, languageSwitch ? (jsx(LanguageSwitch, Object.assign({ linkNavElement: linkNavElement, openIcon: openIcon, closeIcon: closeIcon, isMobileView: isMobileView }, languageSwitch))) : null, search && !isMobileView ? (jsx("li", Object.assign({ className: "cds-global-header__navigation-search\n                        cds-global-header__navigation-icon" }, { children: jsx(SearchBlock, Object.assign({}, search, { handleBurgerMenu: handleMobileNavigationItemClick })) }))) : null, cartIcon && !isMobileView ? (jsx("li", Object.assign({ className: classNames(cartIconClassName, 'cds-global-header__navigation-icon') }, { children: jsx(CartIcon, Object.assign({}, cartIcon)) }))) : null, profile ? (jsx("li", Object.assign({ className: classNames(profileClassName, {
                        'cds-global-header__navigation-icon': !isMobileView,
                    }) }, { children: !isMobileView ? (jsx(Tooltip, { tooltipWindowClassName: "cds-global-header-profile-window", portalQuerySelector: ".cds-tooltip", label: profile.headline, isTooltipContentInteractive: true, icon: "profile", content: jsx(Profile, Object.assign({}, profile)) })) : (jsx(Profile, Object.assign({}, profile, { isMobileView: isMobileView }))) }))) : null] })) })));
};

const SkipLink = (_a) => {
    var { children, mode } = _a, linkProps = __rest(_a, ["children", "mode"]);
    return (jsx(Section, Object.assign({ element: "div", mode: mode, className: "cds-skip-link" }, { children: jsx(Link, Object.assign({ className: "cds-skip-link__link" }, linkProps, { children: children })) })));
};

const UtilityLinks = ({ linkNavElement: LinkElement = Link, utilityLinksData = [], utilityLinksName, }) => (jsx("nav", Object.assign({ "aria-label": utilityLinksName, className: "cds-utility-navigation" }, { children: jsx("ul", Object.assign({ className: "cds-utility-navigation__list" }, { children: utilityLinksData.map((_a) => {
            var { href, label, ariaLabel } = _a, rest = __rest(_a, ["href", "label", "ariaLabel"]);
            return (jsx("li", Object.assign({ className: "cds-utility-navigation__item" }, { children: jsx(LinkElement, Object.assign({ className: "cds-utility-navigation__link", href: href, variant: "subtle", size: "small", "aria-label": ariaLabel }, rest, { children: label })) }), label));
        }) })) })));

const useBurgerMenu = (headerReference) => {
    const DOCUMENT_LOCK_CLASSNAME = 'cds-scroll-locked';
    const { isMobileView } = useBrowserScreen();
    const [isOpen, setIsOpen] = useState(false);
    const [burgerOffsetTop, setBurgerOffsetTop] = useState(0);
    const burgerBtnRef = useRef(null);
    const handleBurgerClick = (e) => {
        e.preventDefault();
        const headerEl = headerReference.current;
        if (headerEl) {
            setBurgerOffsetTop(headerEl.getBoundingClientRect().top + headerEl.offsetHeight);
        }
        setIsOpen(!isOpen);
    };
    const handleMobileNavigationItemClick = () => {
        if (isMobileView)
            setIsOpen(!isOpen);
    };
    const handleMobileLogoClick = () => {
        setIsOpen(false);
    };
    const handler = useCallback(() => {
        if (!isMobileView) {
            setIsOpen(false);
        }
    }, [isMobileView]);
    useEffect(() => {
        setIsOpen(false);
    }, []);
    useEffect(() => {
        if (isOpen) {
            document.documentElement.classList.add(DOCUMENT_LOCK_CLASSNAME);
        }
        else {
            document.documentElement.classList.remove(DOCUMENT_LOCK_CLASSNAME);
        }
        if (burgerBtnRef.current) {
            burgerBtnRef.current.focus();
        }
    }, [isOpen]);
    useEffect(() => {
        window.addEventListener('resize', handler);
        window.addEventListener('orientationchange', handler);
        return () => {
            window.removeEventListener('resize', handler);
            window.removeEventListener('orientationchange', handler);
        };
    }, [handler]);
    return {
        isOpen,
        burgerBtnRef,
        handleBurgerClick,
        burgerOffsetTop,
        handleMobileNavigationItemClick,
        handleMobileLogoClick,
    };
};

const useStickyObserver = (reference, callback) => {
    const [isSticky, setSticky] = useState(false);
    useEffect(() => {
        const stickyElm = reference.current;
        if (stickyElm && window) {
            const observer = new IntersectionObserver(([e]) => {
                setSticky(e.intersectionRatio < 1);
                if (e.intersectionRatio === 1 && callback) {
                    callback(stickyElm === null || stickyElm === void 0 ? void 0 : stickyElm.dataset.target);
                }
            }, { threshold: [1, 0] });
            observer.observe(stickyElm);
            return () => {
                observer.unobserve(stickyElm);
            };
        }
        return undefined;
    }, [reference, callback]);
    return [isSticky];
};

const GlobalHeader = ({ className, logo, isHeaderSticky = false, skipLink, utilityLinksData, utilityLinksName, mode, linkNavElement, onClickNavItem, burgerToggleLabel, burgerOpenLabel, burgerCloseLabel, burgerOpenIcon, burgerCloseIcon, navigationName, navigationData, activeUrlPath, moreBtn, openIcon, closeIcon, languageSwitch, search, cartIcon, profile, renderOnStart, renderOnEnd, renderOnBurgerPopupStart, renderOnBurgerPopupEnd, uid, maxVisibleNavItems = 0, }) => {
    const headerRef = useRef(null);
    const [isSticky] = useStickyObserver(headerRef);
    const [isJS] = useJS();
    const { isOpen, burgerBtnRef, handleBurgerClick, burgerOffsetTop, handleMobileNavigationItemClick, handleMobileLogoClick, } = useBurgerMenu(headerRef);
    const { isMobileView } = useBrowserScreen();
    useEffect(() => {
        const htmlObject = document.getElementsByTagName('html')[0];
        if (isHeaderSticky) {
            htmlObject.classList.add('has-sticky-nav');
        }
        else {
            htmlObject.classList.remove('has-sticky-nav');
        }
    }, [isHeaderSticky]);
    return (jsxs(Section, Object.assign({ ref: headerRef, element: "header", id: "open-menu", mode: mode, removeDuplicateSpacing: false, "data-status": isJS ? 'js' : 'no-js', className: classNames('cds-global-header', className, {
            [`cds-global-header--is-sticky`]: isSticky && isHeaderSticky,
            [`cds-global-header--sticky-mode`]: isHeaderSticky,
            [`cds-global-header--burger-opened`]: isMobileView && isOpen,
        }), "data-component-uid": uid }, { children: [jsx(SkipLink, Object.assign({}, skipLink)), jsx(Container, Object.assign({ maxWidth: "max-grid" }, { children: jsx(FocusTrapWrapper, Object.assign({ isOpen: isOpen, isMobileView: isMobileView, isJS: isJS }, { children: jsxs("div", Object.assign({ className: "header-portal" }, { children: [renderOnStart, jsxs("div", Object.assign({ className: "cds-global-header__mobile-main" }, { children: [logo && (jsx(Logo, { className: "cds-global-header__mobile-logo", image: logo.imageSmall, link: logo.link, onClick: handleMobileLogoClick })), isMobileView && (jsxs("ul", Object.assign({ className: "cds-global-header__navigation-wrapper" }, { children: [search ? (jsx("li", Object.assign({ className: classNames('cds-global-header__navigation-search', 'cds-global-header__navigation-icon') }, { children: jsx(SearchBlock, Object.assign({}, search, { handleBurgerMenu: handleMobileNavigationItemClick })) }))) : null, cartIcon ? (jsx("li", Object.assign({ className: classNames(cartIconClassName, 'cds-global-header__navigation-icon') }, { children: jsx(CartIcon, Object.assign({}, cartIcon)) }))) : null, jsx("li", { children: jsx(BurgerOpener, { burgerToggleLabel: burgerToggleLabel, burgerOpenIcon: burgerOpenIcon, burgerCloseIcon: burgerCloseIcon, burgerOpenLabel: burgerOpenLabel, isJS: isJS, handleBurgerClick: handleBurgerClick, isOpen: isOpen, burgerBtnRef: burgerBtnRef, isMobileView: isMobileView }) })] })))] })), isOpen || !isMobileView || !isJS ? (jsxs("div", Object.assign({ style: { top: `${burgerOffsetTop}px` }, className: "cds-global-header__burger-popup" }, { children: [renderOnBurgerPopupStart, jsx(BurgerCloser, { burgerCloseIcon: burgerCloseIcon, burgerCloseLabel: burgerCloseLabel, isJS: isJS }), shouldBeRendered(utilityLinksData, [!isJS, !isMobileView], 'or') && (jsx("div", Object.assign({ className: "cds-global-header__top" }, { children: jsx(UtilityLinks, { utilityLinksData: utilityLinksData, linkNavElement: linkNavElement, utilityLinksName: utilityLinksName }) }))), jsx("div", Object.assign({ className: "cds-global-header__desktop-main" }, { children: jsx(Navigation, { navigationName: navigationName, navigationData: navigationData, activeUrlPath: activeUrlPath, linkNavElement: linkNavElement, handleMobileNavigationItemClick: handleMobileNavigationItemClick, logo: logo, moreBtn: moreBtn, openIcon: openIcon, closeIcon: closeIcon, languageSwitch: languageSwitch, search: search, cartIcon: cartIcon, onClickNavItem: onClickNavItem, profile: profile, maxVisibleNavItems: maxVisibleNavItems }) })), shouldBeRendered(utilityLinksData, [isJS, isMobileView], 'and') && (jsx("div", Object.assign({ className: "cds-global-header__bottom" }, { children: jsx(UtilityLinks, { utilityLinksData: utilityLinksData, linkNavElement: linkNavElement, utilityLinksName: utilityLinksName }) }))), renderOnBurgerPopupEnd] }))) : null, renderOnEnd] })) })) }))] })));
};

export { GlobalHeader };
